import { useEffect, useState } from 'react';
import { counted, fill, t, type DealDeletion } from '@bms2/shared';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import { get, post, DEALS_PATH } from './api';
import styles from './RecordPage.module.css';

/** 🔒 What the Delete Deal window reads when it opens, and the delete itself (decision 12700). */
export const dealDeletionPath = (dealId: string) => `${DEALS_PATH}/${dealId}/deletion`;
export const dealDeletePath = (dealId: string) => `${DEALS_PATH}/${dealId}/delete`;
/** 🔒 Decision 12970: the `admin.operations` holder's Delete Anyway, to the Trash. */
export const dealDeleteAnywayPath = (dealId: string) => `${DEALS_PATH}/${dealId}/delete-anyway`;

interface DealDeleteModalProps {
  open: boolean;
  dealId: string;
  apiBaseUrl?: string;
  onCancel: () => void;
  /** The delete stood; the page leaves for the Deals list. */
  onDeleted: () => void;
}

/**
 * 🔒 The Delete Deal window (decision 12700; T3820). It reads what the Deal
 * holds when it opens: a Deal holding a document draws Delete disabled with
 * the sentence naming what holds it, which points to Mark Lost as Entered in
 * Error. A bare Deal's window names it and counts the notes, files and
 * contacts that go with it. A refusal from the server reads inside the window
 * (decision 3063), which stays open.
 */
export function DealDeleteModal({ open, dealId, apiBaseUrl = '', onCancel, onDeleted }: DealDeleteModalProps) {
  const [deletion, setDeletion] = useState<DealDeletion | null>(null);
  const [unread, setUnread] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    let live = true;
    void get<DealDeletion>(dealDeletionPath(dealId), apiBaseUrl).then((answer) => {
      if (!live) return;
      if (answer.ok && answer.data) setDeletion(answer.data);
      else setUnread(true);
    });
    return () => {
      live = false;
    };
  }, [open, dealId, apiBaseUrl]);
  const holds = deletion?.holds ?? null;
  const ready = deletion !== null && holds === null && !busy;
  const remove = async () => {
    if (!ready) return;
    setRefusal(null);
    setBusy(true);
    const answer = await post<{ deleted: true }>(dealDeletePath(dealId), {}, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setRefusal(answer.message);
    onDeleted();
  };
  const notReady = unread ? t('deal.delete.unread') : holds ?? (deletion === null ? t('deal.delete.reading') : undefined);
  // 🔒 Decision 12970: Delete Anyway is drawn only where the server answered it, to the `admin.operations` holder, and only while Delete refuses.
  const anyway = holds !== null ? (deletion?.anyway ?? null) : null;
  const removeAnyway = async () => {
    if (!anyway || anyway.refusal || busy) return;
    setRefusal(null);
    setBusy(true);
    const answer = await post<{ deleted: true }>(dealDeleteAnywayPath(dealId), {}, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setRefusal(answer.message);
    onDeleted();
  };
  return (
    <Modal
      open={open}
      title={t('deal.delete.title')}
      onCancel={onCancel}
      dataModal="deal-delete"
      actions={[
        ...(anyway ? [{ kind: 'destructiveSecondary' as const, label: t('deal.delete.anyway'), onAct: () => void removeAnyway(), disabled: anyway.refusal !== null || busy, ...(anyway.refusal ? { disabledReason: anyway.refusal } : {}), destructive: { subject: anyway.subject, phrase: t('modal.deletePhrase'), toTrash: true } }] : []),
        { kind: 'primary', label: t('deal.delete.confirm'), onAct: () => remove(), disabled: !ready, ...(notReady ? { disabledReason: notReady } : {}) },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {refusal ? <AlertBanner tone="error" message={refusal} action={{ label: t('modal.dismiss'), onAction: () => setRefusal(null) }} /> : null}
      {unread ? <p className={styles.problem} data-deal-delete-unread="true">{t('deal.delete.unread')}</p> : null}
      {!unread && deletion === null ? <p className={styles.hint}>{t('deal.delete.reading')}</p> : null}
      {deletion && holds ? (
        <p className={styles.problem} data-deal-delete-holds="true">{holds}</p>
      ) : null}
      {anyway ? (
        <p className={styles.hint} data-deal-delete-anyway={anyway.refusal ? 'refused' : 'stands'}>{anyway.refusal ?? fill(t('deal.delete.anywayLead'), { subject: anyway.subject })}</p>
      ) : null}
      {deletion && !holds ? (
        <div data-deal-delete-goes="true">
          <p>{fill(t('deal.delete.lead'), { name: deletion.name })}</p>
          <p className={styles.hint}>{fill(t('deal.delete.goes'), { notes: counted(deletion.goes.notes, 'note'), files: counted(deletion.goes.files, 'file'), contacts: counted(deletion.goes.contacts, 'contact') })}</p>
        </div>
      ) : null}
    </Modal>
  );
}
